import React, { useState, useEffect, useCallback } from 'react';
import {
  getDealers, createDealer, updateDealer, deleteDealer,
  getDealerUsers, createDealerUser, updateDealerUser,
  resetDealerUserPassword, deleteDealerUser,
} from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Buildings, Pencil, Trash, UsersThree, EnvelopeSimple,
  Key, ShieldSlash, ShieldCheck, MagnifyingGlass, CopySimple, ClockCounterClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const emptyCompany = {
  name: '', email: '', phone: '', address: '', contact_person: '', notes: '',
};

const emptyStaff = {
  email: '', name: '', phone: '', dealer_role: 'staff', password: '', send_welcome_email: true,
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

const DealersPage = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [companyDialog, setCompanyDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyForm, setCompanyForm] = useState(emptyCompany);
  const [submitting, setSubmitting] = useState(false);

  const [staffDialog, setStaffDialog] = useState(false);
  const [staffCompany, setStaffCompany] = useState(null); // dealer object
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffForm, setStaffForm] = useState(emptyStaff);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [creatingStaff, setCreatingStaff] = useState(false);
  const [tempPasswordInfo, setTempPasswordInfo] = useState(null); // {email, password, action}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getDealers();
      setDealers(r.data || []);
    } catch (e) {
      toast.error('Failed to load dealers');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredDealers = (dealers || []).filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.name || '').toLowerCase().includes(q)
      || (d.email || '').toLowerCase().includes(q)
      || (d.contact_person || '').toLowerCase().includes(q);
  });

  // -------- Company create / edit ---------
  const openCreateCompany = () => {
    setEditingCompany(null);
    setCompanyForm(emptyCompany);
    setCompanyDialog(true);
  };
  const openEditCompany = (d) => {
    setEditingCompany(d);
    setCompanyForm({
      name: d.name || '', email: d.email || '', phone: d.phone || '',
      address: d.address || '', contact_person: d.contact_person || '', notes: d.notes || '',
    });
    setCompanyDialog(true);
  };
  const submitCompany = async (e) => {
    e.preventDefault();
    if (!companyForm.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      if (editingCompany) {
        await updateDealer(editingCompany.id, companyForm);
        toast.success('Dealer updated');
      } else {
        await createDealer(companyForm);
        toast.success('Dealer created');
      }
      setCompanyDialog(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save dealer');
    } finally { setSubmitting(false); }
  };
  const removeCompany = async (d) => {
    if (!window.confirm(`Delete dealer "${d.name}"? This will also delete all its staff logins.`)) return;
    try {
      await deleteDealer(d.id);
      toast.success('Dealer deleted');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  // -------- Staff management ---------
  const openStaff = async (d) => {
    setStaffCompany(d);
    setStaffDialog(true);
    setStaffLoading(true);
    setAddStaffOpen(false);
    setStaffForm(emptyStaff);
    try {
      const r = await getDealerUsers(d.id);
      setStaffUsers(r.data || []);
    } catch (err) {
      toast.error('Failed to load staff users');
    } finally { setStaffLoading(false); }
  };
  const reloadStaff = async () => {
    if (!staffCompany) return;
    try {
      const r = await getDealerUsers(staffCompany.id);
      setStaffUsers(r.data || []);
    } catch { /* ignore */ }
  };
  const submitStaff = async (e) => {
    e.preventDefault();
    if (!staffForm.email || !staffForm.name) {
      toast.error('Email and Name are required'); return;
    }
    setCreatingStaff(true);
    try {
      const payload = { ...staffForm };
      if (!payload.password) delete payload.password;
      const r = await createDealerUser(staffCompany.id, payload);
      const info = r.data || {};
      if (info.temp_password) {
        // Email failed or wasn't requested → expose password once for admin
        setTempPasswordInfo({
          email: info.email, password: info.temp_password,
          action: info.welcome_email_sent ? 'sent' : 'manual',
        });
      } else {
        toast.success(`Staff created${info.welcome_email_sent ? ' — welcome email sent' : ''}`);
      }
      setStaffForm(emptyStaff);
      setAddStaffOpen(false);
      await reloadStaff();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create staff');
    } finally { setCreatingStaff(false); }
  };
  const toggleSuspend = async (u) => {
    try {
      await updateDealerUser(staffCompany.id, u.id, { is_suspended: !u.is_suspended });
      toast.success(u.is_suspended ? 'User reactivated' : 'User suspended');
      await reloadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };
  const changeRole = async (u, role) => {
    try {
      await updateDealerUser(staffCompany.id, u.id, { dealer_role: role });
      await reloadStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };
  const resetPwd = async (u) => {
    if (!window.confirm(`Reset password for ${u.email}? A new password will be emailed (or shown to you if email fails).`)) return;
    try {
      const r = await resetDealerUserPassword(staffCompany.id, u.id, { send_email: true });
      const data = r.data || {};
      if (data.temp_password) {
        setTempPasswordInfo({ email: u.email, password: data.temp_password, action: 'reset' });
      } else {
        toast.success('Password reset — email sent');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset');
    }
  };
  const removeStaff = async (u) => {
    if (!window.confirm(`Delete login for ${u.email}? This cannot be undone.`)) return;
    try {
      await deleteDealerUser(staffCompany.id, u.id);
      toast.success('Staff deleted');
      await reloadStaff();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="dealers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Buildings weight="duotone" size={28} className="text-primary" />
            Dealers
          </h1>
          <p className="text-sm text-muted-foreground">
            Manage B2B dealer companies, their portal logins, and staff accounts.
          </p>
        </div>
        <Button onClick={openCreateCompany} data-testid="create-dealer-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Dealer
        </Button>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <MagnifyingGlass className="text-muted-foreground" />
        <Input
          placeholder="Search by name, email, contact person…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="dealers-search-input"
        />
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-10">Loading…</div>
      ) : filteredDealers.length === 0 ? (
        <div className="border border-dashed rounded-md p-10 text-center text-muted-foreground">
          No dealers yet. Click <strong>Add Dealer</strong> to create one.
        </div>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Company</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium text-center">Staff</th>
                <th className="px-4 py-3 font-medium text-center">Orders</th>
                <th className="px-4 py-3 font-medium">Last login</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDealers.map(d => (
                <tr key={d.id} className="border-t hover:bg-muted/20" data-testid={`dealer-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {d.contact_person ? `Contact: ${d.contact_person}` : '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {d.email && <div>{d.email}</div>}
                    {d.phone && <div className="text-muted-foreground">{d.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="secondary">{d.user_count || 0}</Badge>
                  </td>
                  <td className="px-4 py-3 text-center">{d.order_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    <ClockCounterClockwise size={14} className="inline mr-1" />
                    {formatDate(d.last_login_at)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm" variant="outline"
                      className="mr-1"
                      onClick={() => openStaff(d)}
                      data-testid={`dealer-staff-btn-${d.id}`}
                    >
                      <UsersThree size={16} className="mr-1" /> Staff
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEditCompany(d)} title="Edit">
                      <Pencil size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeCompany(d)} title="Delete">
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Company create / edit dialog */}
      <Dialog open={companyDialog} onOpenChange={setCompanyDialog}>
        <DialogContent className="max-w-lg" data-testid="dealer-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingCompany ? 'Edit dealer' : 'Add new dealer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCompany} className="space-y-4">
            <div>
              <Label>Company name *</Label>
              <Input
                value={companyForm.name}
                onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })}
                required data-testid="dealer-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input
                  type="email" value={companyForm.email}
                  onChange={e => setCompanyForm({ ...companyForm, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={companyForm.phone}
                  onChange={e => setCompanyForm({ ...companyForm, phone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Contact person</Label>
              <Input
                value={companyForm.contact_person}
                onChange={e => setCompanyForm({ ...companyForm, contact_person: e.target.value })}
              />
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={companyForm.address}
                onChange={e => setCompanyForm({ ...companyForm, address: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={companyForm.notes}
                onChange={e => setCompanyForm({ ...companyForm, notes: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCompanyDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="dealer-submit-btn">
                {submitting ? 'Saving…' : (editingCompany ? 'Save' : 'Create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Staff dialog */}
      <Dialog open={staffDialog} onOpenChange={setStaffDialog}>
        <DialogContent className="max-w-3xl" data-testid="dealer-staff-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersThree weight="duotone" />
              Staff — {staffCompany?.name}
            </DialogTitle>
          </DialogHeader>

          {staffLoading ? (
            <div className="text-center text-muted-foreground py-6">Loading…</div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  All users below can log into the dealer portal and share the same orders, chat thread, and beneficiaries.
                </p>
                {!addStaffOpen && (
                  <Button size="sm" onClick={() => { setStaffForm(emptyStaff); setAddStaffOpen(true); }} data-testid="add-staff-btn">
                    <Plus size={16} className="mr-1" /> Add staff
                  </Button>
                )}
              </div>

              {addStaffOpen && (
                <form onSubmit={submitStaff} className="border rounded-md p-3 space-y-3 bg-muted/20">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email" value={staffForm.email}
                        onChange={e => setStaffForm({ ...staffForm, email: e.target.value })}
                        required data-testid="staff-email-input"
                      />
                    </div>
                    <div>
                      <Label>Full name *</Label>
                      <Input
                        value={staffForm.name}
                        onChange={e => setStaffForm({ ...staffForm, name: e.target.value })}
                        required data-testid="staff-name-input"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={staffForm.phone}
                        onChange={e => setStaffForm({ ...staffForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <Select
                        value={staffForm.dealer_role}
                        onValueChange={v => setStaffForm({ ...staffForm, dealer_role: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">Owner</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="staff">Staff</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Password <span className="text-xs text-muted-foreground">(leave empty to auto-generate)</span></Label>
                      <Input
                        type="text"
                        value={staffForm.password}
                        onChange={e => setStaffForm({ ...staffForm, password: e.target.value })}
                        placeholder="(auto-generated if blank)"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t pt-3">
                    <label className="flex items-center gap-2 text-sm" data-testid="welcome-email-toggle">
                      <Switch
                        checked={staffForm.send_welcome_email}
                        onCheckedChange={v => setStaffForm({ ...staffForm, send_welcome_email: v })}
                      />
                      <EnvelopeSimple size={16} />
                      Send welcome email with login link
                    </label>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => setAddStaffOpen(false)}>Cancel</Button>
                      <Button type="submit" size="sm" disabled={creatingStaff} data-testid="staff-create-btn">
                        {creatingStaff ? 'Creating…' : 'Create staff'}
                      </Button>
                    </div>
                  </div>
                </form>
              )}

              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium">User</th>
                      <th className="px-3 py-2 font-medium">Role</th>
                      <th className="px-3 py-2 font-medium">Logins</th>
                      <th className="px-3 py-2 font-medium">Last login</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffUsers.length === 0 && (
                      <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No staff yet</td></tr>
                    )}
                    {staffUsers.map(u => (
                      <tr key={u.id} className="border-t" data-testid={`staff-row-${u.id}`}>
                        <td className="px-3 py-2">
                          <div className="font-medium">{u.name}</div>
                          <div className="text-xs text-muted-foreground">{u.email}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Select value={u.dealer_role || 'staff'} onValueChange={v => changeRole(u, v)}>
                            <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">Owner</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="staff">Staff</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2">{u.login_count || 0}</td>
                        <td className="px-3 py-2 text-xs">
                          <div>{formatDate(u.last_login_at)}</div>
                          {u.last_login_ip && <div className="text-muted-foreground">IP {u.last_login_ip}</div>}
                        </td>
                        <td className="px-3 py-2">
                          {u.is_suspended
                            ? <Badge variant="destructive">Suspended</Badge>
                            : <Badge variant="default" className="bg-emerald-600">Active</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <Button size="sm" variant="ghost" title="Reset password" onClick={() => resetPwd(u)}>
                            <Key size={14} />
                          </Button>
                          <Button size="sm" variant="ghost" title={u.is_suspended ? 'Reactivate' : 'Suspend'} onClick={() => toggleSuspend(u)}>
                            {u.is_suspended ? <ShieldCheck size={14} /> : <ShieldSlash size={14} />}
                          </Button>
                          <Button size="sm" variant="ghost" title="Delete" onClick={() => removeStaff(u)}>
                            <Trash size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setStaffDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Temp password reveal (shown when email failed or admin wants to copy) */}
      <Dialog open={!!tempPasswordInfo} onOpenChange={() => setTempPasswordInfo(null)}>
        <DialogContent className="max-w-md" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle>Login credentials</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              {tempPasswordInfo?.action === 'reset'
                ? 'Password reset — copy and share with the user. They should change it on first login.'
                : 'Email could not be delivered (or was not requested). Copy this password and share it securely.'}
            </p>
            <div>
              <Label>Email</Label>
              <Input readOnly value={tempPasswordInfo?.email || ''} />
            </div>
            <div>
              <Label>Temporary password</Label>
              <div className="flex gap-2">
                <Input
                  readOnly value={tempPasswordInfo?.password || ''}
                  className="font-mono"
                />
                <Button
                  type="button" variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(tempPasswordInfo?.password || '');
                    toast.success('Password copied');
                  }}
                >
                  <CopySimple size={16} />
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTempPasswordInfo(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DealersPage;
